import type { ImageSelectorTab, OrgUnit } from '@amzn/journeys-common';
import { gql } from '@apollo/client';
import { SUPPORTED_VIDEO_FILE_TYPES } from 'app.constants';
import { assign, find, isEmpty, isNil, isObjectLike, keys, map, pickBy, reduce, some } from 'es-toolkit/compat';
import { AssetStrategy } from 'features/traffic/campaigns/constants/assets.constants';
import type { AssetIdToUrlMap } from 'features/traffic/campaigns/content/generic.types';
import { shortenMediaCentralUrl } from 'features/traffic/campaigns/content/generic.util';
import { type AssetSearchResult, type TpAssetMapFragment, useRenameAssetMutation } from 'generated/graphql';
import { type Dispatch, useEffect, useMemo } from 'react';
import { Observable, type Observer, type TeardownLogic } from 'rxjs';
import { SortType } from '../filters/filters.util';
import type { MediaAssetModel } from '../traffic/campaigns/content/ContentBlocks/types';
import type { AssetSelectionExisting, GalleryImage, Rect } from './assets.util.types';
import type { ImageSelectorModalAction } from './image-selector-modal.actions';
import type { AssetIdSelection } from './image-selector-modal.types';

/**
 * Checks if the given asset has an image file type.
 *
 * @param {AssetSearchResult} asset - The asset object to check.
 * @returns {boolean} `true` if the asset has a valid image file type (JPG, PNG, or GIF), `false` otherwise.
 *
 */
export const isValidImageFileType = (asset: AssetSearchResult) => asset.name && /\.(jpg|png|gif|jpeg)$/i.test(asset.name);

/**
 * Returns the url for an asset based on the strategy and org unit
 * - if strategy is not "S3", returns first available MC url
 * @param strategy Asset strategy
 * @param asset Asset fragment
 * @param orgUnit marketplace org unit
 * @param shortenUrl whether to shorten the url?
 * @returns url of asset
 */
export const assetUrl = (
  strategy: (typeof AssetStrategy)[keyof typeof AssetStrategy],
  asset: TpAssetMapFragment,
  orgUnit: OrgUnit,
  shortenUrl = false,
): string => {
  if (strategy === AssetStrategy.S3) {
    return asset.s3SignedUrlGet!;
  }

  const mcUrl = asset.mediaCentralUrls?.[orgUnit]?.[0];
  if (!mcUrl && strategy === AssetStrategy.MC_ERROR_MISSING) {
    throw new Error(`Missing Media Central URL for asset ${asset.assetId}`);
  }
  // MC_ALLOW_MISSING
  return shortenUrl ? shortenMediaCentralUrl(mcUrl, orgUnit) : mcUrl;
};

export const getAssetFromMap = (assetMap: AssetIdToUrlMap, assetId?: string, assetType?: string): TpAssetMapFragment => {
  if (!assetId) throw new Error(`Missing ${assetType || 'asset'} ID`);
  const asset = assetMap[assetId];
  if (!asset) throw new Error(`No ${assetType || 'asset'} found for ID ${assetId}`);
  return asset;
};

export const isNilOrEmpty = (value: unknown) => {
  if ((isObjectLike(value) && isEmpty(value)) || isNil(value) || value === '' || Number.isNaN(value as number)) {
    return true;
  }
};

// Current default language selection for MC URLs - used to select marketplace.
export const defaultLanguageSelection = 'US English';

/**
 * Resize the given rect so that its height matches targetHeight, maintaining aspect ratio
 * @param targetHeight
 * @param rect
 */
export function resizeToHeight<T extends Rect>(targetHeight: number, rect: T): T {
  const ratio = rect.width / rect.height;
  const newWidth = ratio * targetHeight;

  return assign({}, rect, {
    height: targetHeight,
    width: newWidth,
  });
}

/**
 * Checks if the provided value is a valid AssetSearchResult object.
 * @param {unknown} value - The value to check.
 * @returns {value is AssetSearchResult} True if the value is a valid AssetSearchResult object, false otherwise.
 */
export const isTypeAssetSearchResult = (value: unknown): value is AssetSearchResult =>
  typeof value === 'object' && value !== null && 'assetId' in value && typeof value?.assetId === 'string';

/**
 * Checks if the provided value is an array of AssetSearchResult objects.
 * @param {unknown} value - The value to check.
 * @returns {value is AssetSearchResult[]} True if the value is an array of AssetSearchResult objects, false otherwise.
 */
export const isTypeAssetSearchResultArray = (value: unknown): value is AssetSearchResult[] =>
  Array.isArray(value) && value.every(isTypeAssetSearchResult);

/**
 * Resize the given rect so that its width matches targetWidth, maintaining aspect ratio
 * @param targetWidth
 * @param rect
 */
export function resizeToWidth<T extends Rect>(targetWidth: number, rect: T): T {
  const ratio = rect.width / rect.height;
  const newHeight = targetWidth / ratio;

  return assign({}, rect, {
    height: newHeight,
    width: targetWidth,
  });
}

/**
 * Resize all rects in the row so that they have the same height (maintaining aspect ratio)
 * @param targetHeight
 * @param rects
 */
function normalizeHeights<T extends Rect>(targetHeight: number, rects: T[]): T[] {
  return map(rects, (rect) => resizeToHeight<T>(targetHeight, rect));
}

/**
 * Place rects into a row until it overflows rowWidth or no rects remain.
 * @param rowWidth
 * @param rects
 */
function packRow(rowWidth: number, rects: Rect[]): Rect[] {
  const rowRects = [];
  let remainingWidth = rowWidth;

  for (let n = 0; n < rects.length; n++) {
    const rect = rects[n];
    if (remainingWidth > 0) {
      rowRects.push(rect);
      remainingWidth = remainingWidth - rect.width;
    } else {
      break;
    }
  }

  return rowRects;
}

/**
 * Resize the rects in the row (maintaining aspect ratio of rects) to make the row fit the given width
 * @param rowWidth The target width
 * @param row The rects to resize
 */
function squishRow<T extends Rect>(rowWidth: number, row: T[]): T[] {
  const overWidth = reduce(row, (total, { width }) => total + width, 0);
  const widthScale = rowWidth / overWidth;
  return map(row, (rect) => {
    const newRectWidth = rect.width * widthScale;
    if (newRectWidth < rect.width) {
      return resizeToWidth<T>(newRectWidth, rect);
    }
    return rect;
  });
}

/**
 * Partition the given rects into rows of max rowWidth
 * @param rowWidth The max row width
 * @param rects The rects to partition
 */
function partitionIntoRows<T extends Rect>(rowWidth: number, rects: T[]): T[][] {
  const rows: T[][] = [];
  let remainingRects = rects;
  while (remainingRects.length > 0) {
    const row = packRow(rowWidth, remainingRects) as T[];
    if (row.length === 0) {
      break;
    }
    rows.push(row);
    remainingRects = remainingRects.slice(row.length);
  }

  return rows;
}

/**
 * Round the given rect width and heights to the nearest integer
 * @param rects
 */
function roundRects<T extends Rect>(rects: T[]): T[] {
  return map(rects, (rect) =>
    assign({}, rect, {
      height: Math.round(rect.height),
      width: Math.round(rect.width),
    }),
  );
}

export const assetSelectorIdealRowHeight = 150;

/**
 * Partition the given images into rows of the same width.
 * The last row may have less than the row width, if there are not enough images to fill it.
 * @param rowWidth The images
 * @param images The max row width
 */
export function getGalleryRows(rowWidth: number, images: GalleryImage[]): GalleryImage[][] {
  const normalizedImages = normalizeHeights(assetSelectorIdealRowHeight, images);
  const rows = partitionIntoRows(rowWidth, normalizedImages);
  return map(rows, (row) => roundRects(squishRow(rowWidth, row)));
}

export type UseClickOutsideOpts = {
  /**
   * A CSS selector that determines the set of elements that are "inside". Any click
   * on an element outside of the set is considered a click "outside".
   */
  insideSelector: string | (() => string);
  /**
   * Start listening for outside clicks when this boolean data property changes to true
   */
  listenWhen: string;
  /**
   * Callback for when a click outside happens
   * @param event
   */
  onClickedOutside?: (event: MouseEvent | TouchEvent) => void;

  /**
   * Should the mixin preventDefault on the event or allow it?
   */
  allowDefault?: boolean;
};

function getClickEventName() {
  return 'ontouchstart' in document.documentElement ? 'touchstart' : 'click';
}

/**
 * A mixin that detects clicks outside a specified set of elements. Useful for dropdown menus, for example.
 */
export function useClickOutside({ insideSelector, listenWhen, onClickedOutside, allowDefault }: UseClickOutsideOpts) {
  useEffect(() => {
    const eventHandler = (event: MouseEvent | TouchEvent) => {
      if (event.target instanceof Element) {
        const selector = typeof insideSelector === 'string' ? insideSelector : insideSelector.call(null);
        const insideElems = Array.from(document.querySelectorAll(selector));
        const clickedInside = event.target.matches(selector) || some(insideElems, (elem) => elem.contains(event.target as Node));

        if (!clickedInside) {
          if (!allowDefault) {
            event.preventDefault();
          }
          removeListener();

          if (onClickedOutside) {
            onClickedOutside.call(null, event);
          }
        }
      }
    };

    const addListener = () => {
      document.addEventListener(getClickEventName(), eventHandler, {
        capture: true,
      });
    };

    const removeListener = () => {
      document.removeEventListener(getClickEventName(), eventHandler, {
        // must specify capture here to match the addEventListener, otherwise listener is not removed
        capture: true,
      });
    };

    if (listenWhen) {
      addListener();
    } else {
      removeListener();
    }

    return removeListener;
  }, [
    allowDefault,
    insideSelector,
    listenWhen,
    onClickedOutside,
  ]);
}

export type AssetSelectionFromFile = {
  assetFile: File;
};

/**
 * The asset selector modal could return an id of an existing asset, a URL to download the asset from and then
 * upload it as a new asset, or a file from the user's file system
 */
export type AssetSelection = AssetSelectionExisting | AssetSelectionFromFile;

/**
 * Get the image file from a URL. Assumes the URL points to an image.
 *
 * If the GET request to the image errors out, the promise will reject. This could happen if the server
 * doesn't allow CORS requests, for example.
 *
 * @param url
 * @return A promise that resolves to an image file at that URL
 */
export function getImageFileFromUrl(url: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const req = new XMLHttpRequest();
    req.open('GET', url);
    req.responseType = 'blob';
    req.addEventListener('load', () => {
      const imgBlob = req.response as Blob;
      resolve(
        new File(
          [
            imgBlob,
          ],
          url,
          {
            type: imgBlob.type,
          },
        ),
      );
    });
    req.addEventListener('error', () => {
      reject('Error trying to get image.');
    });
    req.send();
  });
}

export function isExistingAssetSelection(selection: AssetSelection): selection is AssetSelectionExisting {
  return (selection as AssetSelectionExisting).asset !== undefined;
}

export function isFileAssetSelection(selection: AssetSelection): selection is AssetSelectionFromFile {
  return (selection as AssetSelectionFromFile).assetFile !== undefined;
}

export function assetToGalleryImage(asset: AssetSearchResult): GalleryImage {
  return {
    height: asset.assetMetadata?.height ?? 0,
    id: asset.assetId,
    meta: asset,
    name: asset.name,
    origHeight: asset.assetMetadata?.height ?? 0,
    origWidth: asset.assetMetadata?.width ?? 0,
    src: asset.s3SignedUrlGet ?? '', // TODO change GQL schema to have required params
    width: asset.assetMetadata?.width ?? 0,
  };
}

/**
 * A hook that encapsulates image renaming logic for components that use the image gallery.
 */
export function useImageRenaming() {
  const [renameAsset] = useRenameAssetMutation();
  const onImageNameChanged = ({ id, name }: { id: string; name: string }) => {
    renameAsset({
      optimisticResponse: () => ({
        renameAsset: {
          code: 'SUCCESS',
          message: 'renamed',
          success: true,
        },
      }),
      update: (cache) => {
        cache.writeFragment({
          data: {
            __typename: 'AssetSearchResult',
            assetId: id,
            name,
          },
          fragment: gql`
            fragment MyAsset on AssetSearchResult {
              name
            }
          `,
        });
      },
      variables: {
        input: {
          assetId: id,
          name,
        },
      },
    });
  };

  return {
    onImageNameChanged,
  };
}

/**
 * A hook that encapsulates image gallery multiselection logic for the image selector modal tabs
 * @param selectedAssets
 * @param dispatch
 * @param assetSearchResults
 * @param from
 */
export function useImageGallerySelection({
  selectedAssets,
  dispatch,
  assetSearchResults,
  from,
}: {
  selectedAssets: AssetSelectionExisting[];
  dispatch: Dispatch<ImageSelectorModalAction>;
  assetSearchResults: AssetSearchResult[];
  from: ImageSelectorTab;
}) {
  const images = useMemo(
    () => map(assetSearchResults, (asset) => assetToGalleryImage(asset)),
    [
      assetSearchResults,
    ],
  );

  const selectedAssetIds = useMemo(
    () =>
      reduce(
        selectedAssets,
        (assetIds, asset) => ({
          ...assetIds,
          [asset.asset.assetId]: true,
        }),
        {} as AssetIdSelection,
      ),
    [
      selectedAssets,
    ],
  );

  const onSelectionChanged = (selection: { [imgId: string]: boolean }) => {
    const selectedIds = keys(pickBy(selection, Boolean));
    const selectedAssets = [];
    for (let i = 0; i < selectedIds.length; i++) {
      const assetId = selectedIds[i];
      const asset = find(images, (img: GalleryImage) => img.id === assetId);
      if (asset) {
        selectedAssets.push({
          asset: asset?.meta,
        });
      }
    }

    dispatch({
      from,
      selectedAssets,
      type: 'selectedAssetsChanged',
    });
  };

  return {
    images,
    onSelectionChanged,
    selectedAssetIds,
  };
}

//List of Asset Filtering Groups
export const assetGroupList = [
  'ProductLine',
  'Device',
  'Platform',
  'Dimension',
  'Owner',
  'Status',
  'Tag',
];

export const imageSortOptions = [
  SortType.MostRecent,
  SortType.ProductLine,
  SortType.Platform,
  SortType.Device,
  SortType.Owner,
  SortType.Status,
];

export const imageGalleryMaxHeight = 150;

/**
 * An object containing the loaded and total bytes of the request and the key of the S3 object.
 * Note that total may be undefined until the payload size is known.
 */
export type UploadProgress = {
  loaded?: number;
  total?: number;
  done: boolean;
};

/**
 * Upload a file to s3 at the given signedUrl.
 * @param file
 * @param signedUrl
 * @return A stream of progress events
 */
export function uploadFileToS3({ file, signedUrl }: { file: File; signedUrl: string }): Observable<UploadProgress> {
  return Observable.create((observer: Observer<UploadProgress>): TeardownLogic => {
    const uploadReq = new XMLHttpRequest();
    uploadReq.upload.addEventListener('load', (evt) => {
      const status = uploadReq.status;
      if (status < 200 || status > 299) {
        // if got a non 2xx response, treat it as an error
        // TODO - Fix this, uploads are succeeding but the status is a single digit causing this error to be displayed
        // observer.error(new Error('Got non-successful response code ' + status));
      }

      const progObj: UploadProgress = {
        done: true,
        loaded: evt.loaded,
        total: evt.total,
      };
      observer.next(progObj);
      observer.complete();
    });
    uploadReq.upload.addEventListener('progress', (evt) => {
      const progObj: UploadProgress = {
        done: false,
        loaded: evt.loaded,
        total: evt.total,
      };
      observer.next(progObj);
    });
    uploadReq.upload.addEventListener('error', (evt) => {
      observer.error(evt);
    });

    try {
      uploadReq.open('PUT', signedUrl);
      uploadReq.setRequestHeader('Content-Type', file.type);
      uploadReq.send(file);
    } catch (e) {
      observer.error(e);
    }

    // if user cancels the upload, abort the XHR via the observable's unsubscribe function
    // TODO - fix this, it's currently causing a runtime exception, probably because abort() gets called every time.
    // return () => uploadReq.abort();
  });
}

/**
 * Measure the dimensions of an image/video file using modern browser API.
 * @param file
 */
export async function measureAssetDimensions(file: File): Promise<Rect> {
  const url = URL.createObjectURL(file);
  if (SUPPORTED_VIDEO_FILE_TYPES.includes(file.type)) {
    const video = document.createElement('video');
    return await new Promise<{
      width: number;
      height: number;
    }>((resolve, reject) => {
      video.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(url);
        resolve({
          height: video.videoHeight,
          width: video.videoWidth,
        });
      });

      video.addEventListener('error', () => {
        console.error('Could not get dimensions for video ', file.name);
        reject();
      });

      const source = document.createElement('source');
      source.src = url;
      video.appendChild(source);
    });
  }
  const img = new Image();
  return await new Promise<{
    width: number;
    height: number;
  }>((resolve, reject) => {
    img.addEventListener('load', () => {
      URL.revokeObjectURL(url);
      resolve({
        height: img.height,
        width: img.width,
      });
    });

    img.addEventListener('error', () => {
      console.error('Could not get dimensions for image ', file.name);
      reject();
    });

    img.src = url;
  });
}

/**
 * Gets dimensions of an image from its base64 string
 * @param base64AssetString The base64 image data
 * @returns A promise that resolves to the image dimensions
 */
export const getImageDimensionsFromBase64 = (base64AssetString: string): Promise<Rect> => {
  // Check if running in a browser environment where Image is available
  if (window?.Image) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          height: img.height,
          width: img.width,
        });
      };
      img.onerror = (error) => {
        console.error(error);
        const errorMessage = error instanceof Error ? error.message : 'Invalid base64 value given';
        reject(new Error(`Failed to load image: ${errorMessage}`));
      };

      // Use the data URL directly
      img.src = base64AssetString;
    });
  }

  // In non-browser environments, throw an error indicating Image API is required
  return Promise.reject(new Error('Image API is not available. This function requires a browser environment.'));
};

/**
 * Encodes the given asset ID by appending 'JourneysAssetId-' to its front.
 *
 * @param {string} assetId - The asset Id to encode.
 * @returns {string} - The encoded asset Id.
 */
export const encodeAssetId = (assetId: string): string => `JourneysAssetId-${assetId}`;

/**
 * Decodes the given encoded asset ID by removing 'JourneysAssetId-' from its front.
 *
 * @param {string} encodedID - The encoded asset ID to decode.
 * @returns {string} - The original asset ID.
 * @throws {Error} - If the encodedID does not start with 'JourneysAssetId-'.
 */
export const decodeAssetID = (encodedID: string): string => {
  const prefix = 'JourneysAssetId-';
  if (!encodedID.startsWith(prefix)) {
    throw new Error('Invalid encoded asset Id');
  }
  return encodedID.slice(prefix.length);
};

/**
 * Ensures the URL has one of the supported file extensions, adds default if missing
 * @param baseUrl - The URL to check/modify
 * @param supportedExtensions - Array of supported extensions (without dots)
 * @param defaultExtension - Default extension to add if none found
 */
export const ensureFileExtension = (baseUrl: string | undefined, supportedExtensions: string[], defaultExtension: string): string => {
  if (!baseUrl) {
    throw new Error('Base URL is required');
  }

  if (baseUrl.toLowerCase().endsWith('.jpeg')) {
    return `${baseUrl.slice(0, -5)}.jpg`;
  }

  const hasValidExtension = supportedExtensions.some((ext) => baseUrl.toLowerCase().endsWith(`.${ext}`));
  return hasValidExtension ? baseUrl : `${baseUrl}.${defaultExtension}`;
};

/**
 * Gets the complete asset URL with proper file extension
 * @param params.asset - The asset model
 * @param params.assetIdToUrlMap - Map of asset IDs to assets
 * @param params.orgUnit - Organization unit
 * @param params.supportedExtensions - Supported file extensions
 * @param params.defaultExtension - Default extension if none found
 * @param params.assetType - Type of asset for error messages
 */
export const getFormattedAssetUrl = ({
  asset,
  assetIdToUrlMap,
  orgUnit,
  supportedExtensions,
  defaultExtension,
  assetType,
}: {
  asset: MediaAssetModel;
  assetIdToUrlMap: AssetIdToUrlMap;
  orgUnit: OrgUnit;
  supportedExtensions: string[];
  defaultExtension: string;
  assetType?: string;
}): string => {
  const validatedAsset = getAssetFromMap(assetIdToUrlMap, asset.assetId, assetType);
  const baseUrl = assetUrl(AssetStrategy.MC_ERROR_MISSING, validatedAsset, orgUnit, true);
  return ensureFileExtension(baseUrl, supportedExtensions, defaultExtension);
};
